// The changing status of ui widgets (open menus, etc)
// Nothing data-focused should be here, only fleeting ui state

// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux
import { getMessage } from 'util/locale';
import { get, omit, sortBy } from 'lodash';

// utils
import { logError } from 'api/errors';

// sub-ducks
import drawingReducer, { actions as drawingActions } from './drawing';
import locksReducer, { actions as lockActions } from './locks';
import productInputsReducer, {
  setProductInput,
  actions as productInputsActions
} from './product-inputs';
import selectedZonesReducer, {
  removeSelectedZone,
  toggleSelectedZone,
  actions as selectedZonesActions
} from './selected-zones';
import { getManageRoute } from '../../util/routes';

// finders
import { findFertilizersForSubstanceTypeUuid } from 'products/finders';

// loaders -- TODO: convert to finders
import { fetchSeedProductsSearchResultsFromSprout } from 'products/loaders';
import { parseSproutProducts } from 'products/productsParser';
import { findResource } from 'loading/finders';

// selectors
import { getAffiliateById } from 'affiliates/selectors';
import {
  getImageSeasonsOfLayerForField,
  getImageUuidsOfLayerForField,
  getOverlayLayerGroupTypeOfLayer,
  getCropFromActiveImage
} from 'imagery/selectors';
import {
  canAddMoreSeedProductInputs,
  getActiveCrop,
  getActiveCropDfpUuid,
  getActiveFertilizerProductInputs,
  getActiveFieldId,
  getActiveFieldCountryCode,
  getActiveKeypad,
  getActiveScript,
  getActiveScriptId,
  getActiveScriptType,
  getActiveScriptZoneById,
  getActiveSubstanceTypeUuid,
  getActiveSeedProductIds,
  getActiveSeedProductInputs,
  getActiveUserId,
  getActiveZoneId,
  getNumActiveSeedProductInputs,
  getSeedProductInputBySelectId,
  getSelectedOverlayLayer,
  getZoneSelectionMode,
  shouldMakeSeedProductDefault
} from 'current/selectors';

import { getPreferredDeviceForScriptType } from 'devices/selectors';
import { getFertilizerProductsBySubstanceTypeUuid } from 'products/fertility-selectors';
import { getCurrentPath, isEditRoute } from 'routes/selectors';
import {
  getApplicationTypeForScriptType,
  getProductsForScript,
  getScriptTypeForScript,
  getScriptById
} from 'scripts/selectors';
import { getApplicationsForZoneByType } from 'scripts/zones/selectors';
import {
  getProductYearByScriptType,
  isAppMode,
  shouldShowWinfieldLink
} from 'settings/selectors';
import { getLayerValidationResultForToggling } from 'validations/selectors';
import { 
  getProductValidationValues
} from 'user/selectors';

// Imported ducks
import { setApplicationProducts } from 'products';
import {
  clearActiveAffiliateOrganizationId,
  fetchCentroidForActiveFieldId,
  setActiveZoneId,
  setActiveCropForCurrentMapLayer
} from 'current';
import { initializeSeedSelectorSearchParams } from 'edit/seed-product-selector';
import { addErrorToast } from 'messages';
import {
  clearSeedApplicationProductForZones,
  setScriptState,
  updateFertilizerApplicationProductForZones,
  updateProductApplicationProperty,
  updateProductApplicationPropertyForZones,
  updateSeedApplicationProductForZones,
  validateProductApplicationProperty,
  updateProtectionProductForZones
} from 'scripts';

import { validateScriptName } from 'current/validators';
import { setSeedSelectorProductInput } from 'edit/seed-product-selector/product-inputs';
import { setValidationResult } from 'validations';

// constants
import {
  exportTypes,
  inputStates,
  scriptConfigViews,
  selectIdPrefix,
  zoneSelectionModes
} from 'constants/values';
import { MIN_HYBRID_PRODUCT_SEARCH_LENGTH } from 'constants/products';
import { applicationProperties, applicationTypes } from 'constants/scripts';
import { eventTypes } from 'constants/analytics';
import { SIGNAL } from 'constants/fetch';

// util
import { getProductRateLegendForScript } from 'util/formatters/data-viz/product';
import { hasAction } from 'util/duck';
import { updatePlantingPlanParams } from '../../planting-plan-form';

// Action Types
export const typePrefix = 'vra/current/uiwidgets/';
export const actions = [
  'SET_ACTIVE_INCORPORATION_METHOD',
  'SET_ACTIVE_KEYPAD',
  'SET_ACTIVE_SUBSTANCE_TYPE_UUID',
  'SET_CONNECT_TO_AFFILIATE_MODAL',
  'SET_CROP_SELECTION_DOES_NOT_MEET_MODAL',
  'SET_DELETE_MODAL',
  'SET_CONFIRM_MODAL',
  'SET_DUPLICATE_MODAL',
  'SET_EXPORT_OPTIONS_IS_OPEN',
  'SET_SEND_TO_DRIVE_IS_OPEN',
  'SET_HAS_NITRAPYRIN',
  'SET_IS_OVERLAY_ACTIVE',
  'SET_IS_OVERLAY_QUICK_SELECT_ACTIVE',
  'SET_IS_OVERLAY_SELECT_MODAL_ACTIVE',
  'SET_IS_USING_LIQUID',
  'SET_MAP_WAS_CLICKED',
  'SET_PREFERREDDEVICE_MODAL',
  'SET_PRODUCT_RATE_LEGEND',
  'SET_QUITFLOW_MODAL',
  'SET_RENAME_MODAL',
  'SET_ACTIVE_SCRIPT_CONFIG_VIEW',
  'SET_SCRIPT_OPTIONS_IS_OPEN',
  'SET_MORE_DETAILS_IS_OPEN',
  'SET_SEEDPRODUCTSELECTOR_MODAL',
  'SET_SELECTED_OVERLAY_LAYER_GROUP',
  'SET_SELECTED_OVERLAY_LAYER',
  'SET_SELECTED_OVERLAY_SEASON',
  'SET_SELECTED_OVERLAY_UUID',
  'SET_SELECTORGANIZATION_MODAL',
  'SET_SETPRIMARY_MODAL',
  'SET_LAYERINCLUSION_MODAL',
  'SET_EXCLUDED_LAYERS_ERROR_MODAL',
  'SET_EDIT_SCRIPT_EXIT_BUTTON_BEHAVIOR',
  'TOGGLE_DRIVE_MODAL',
  'TOGGLE_ERROR_DRIVE_MODAL',
  'TOGGLE_ZONE_SOILS',
  'SWITCH_NITROGEN_APPLICATION_RATE',
  'SHOW_CREATE_JD_WORK_PLAN_MODAL',
  'HIDE_CREATE_JD_WORK_PLAN_MODAL',

  // analytics
  'CONNECT_TO_AFFILIATE',
  'CREATE_SCRIPT',
  'DELETE_SCRIPT',
  'DUPLICATE_SCRIPT',
  'EXPORT_SCRIPT',
  'OPEN_SEED_PRODUCT_SELECTOR',
  'PREVENT_EDIT_SCRIPT',
  'REMOVE_PRODUCT',
  'RENAME_SCRIPT',
  'RESET_SCRIPT',
  'SAVE_SCRIPT',
  'CANCEL_SCRIPT',
  'SELECT_PRODUCT',
  'SET_SCRIPT_PRIMARY'
].reduce((result, key) => {
  result[key] = `${typePrefix}${key}`;
  return result;
}, {});
export const duplicateTypes = ['EDIT', 'NEW'].reduce((result, key) => {
  result[key] = key.toLowerCase();
  return result;
}, {});

/* TODO - Break out more actions into sub-ducks. I started doing it, and ran out of time. Can follow
   pattern used for drawing, product inputs, and zone selection. Maybe one sub-duck per UI element,
   e.g. renameModal gets a sub-duck. */

// Reducer
export default function reducer( // NOSONAR False-Positive, Reducer Exception
  state = {
    cropSelectionDoesNotMeetModal: false,
    drawing: drawingReducer(),
    exportOptionsIsOpen: { status: false },
    numSeedProductInputs: 1,
    scriptOptionsIsOpen: { status: false },
    isOverlayActive: true,
    isOverlaySelectModalActive: false,
    isUsingLiquid: false,
    locks: locksReducer(),
    productInputs: productInputsReducer(),
    zoneSoils: {},
    activeKeypad: { applicationType: null, property: null, zoneId: null },
    scriptConfigView: scriptConfigViews.SUMMARY,
    selectedZones: selectedZonesReducer(),
    productRateLegend: {},
    mapWasClickedOnZoneSelect: false,
    isDriveModalActive: false,
    isErrorDriveModalActive: false,
    isSendToDriveOpen: false,
    isCreateWorkPlanModalOpen: false
  },
  action = {}
) {
  // Pass actions to sub-Duck reducers as appropriate.
  if (hasAction(drawingActions, action.type)) {
    return { ...state, drawing: drawingReducer(state.drawing, action) };
  } else if (hasAction(productInputsActions, action.type)) {
    return {
      ...state,
      productInputs: productInputsReducer(state.productInputs, action)
    };
  } else if (hasAction(selectedZonesActions, action.type)) {
    return {
      ...state,
      selectedZones: selectedZonesReducer(state.selectedZones, action)
    };
  } else if (hasAction(lockActions, action.type)) {
    return { ...state, locks: locksReducer(state.locks, action) };
  }

  switch (action.type) {
    case actions.SET_DELETE_MODAL:
      return action.payload
        ? { ...state, deleteModal: action.payload }
        : omit(state, ['deleteModal']);
    case actions.SET_CONFIRM_MODAL:
      return action.payload
        ? { ...state, confirmModal: action.payload }
        : omit(state, ['confirmModal']);
    case actions.SET_LAYERINCLUSION_MODAL:
      return action.payload
        ? { ...state, layerInclusionModal: action.payload }
        : omit(state, ['layerInclusionModal']);
    case actions.SET_EXCLUDED_LAYERS_ERROR_MODAL:
      return get(action, 'payload.modalOpen')
        ? { ...state, minimumRequirementsErrorModal: action.payload }
        : omit(state, ['minimumRequirementsErrorModal']);
    case actions.SET_DUPLICATE_MODAL:
      return action.payload
        ? {
          ...state,
          duplicateModal: Object.assign(
            {},
            state.duplicateModal,
            action.payload
          )
        }
        : omit(state, ['duplicateModal']);
    case actions.SET_EXPORT_OPTIONS_IS_OPEN:
      return { ...state, exportOptionsIsOpen: action.payload };
    case actions.SET_SCRIPT_OPTIONS_IS_OPEN:
      return {
        ...state,
        scriptOptionsIsOpen: Object.assign({}, state.scriptOptionsIsOpen, {
          [action.payload.scriptId]: action.payload.status
        })
      };
    case actions.SET_MORE_DETAILS_IS_OPEN:
      return {
        ...state,
        moreDetailsIsOpen: Object.assign({}, state.moreDetailsIsOpen, {
          [action.payload.scriptId]: action.payload.status
        })
      };
    case actions.SET_SEND_TO_DRIVE_IS_OPEN:
      return { ...state, isSendToDriveOpen: action.payload };
    case actions.SET_RENAME_MODAL:
      return action.payload
        ? {
          ...state,
          renameModal: Object.assign({}, state.renameModal, action.payload)
        }
        : omit(state, ['renameModal']);
    case actions.SET_SELECTORGANIZATION_MODAL:
      return action.payload
        ? { ...state, selectAffiliateOrganizationModal: action.payload }
        : omit(state, ['selectAffiliateOrganizationModal']);
    case actions.SET_SETPRIMARY_MODAL:
      return action.payload
        ? { ...state, setPrimaryModal: action.payload }
        : omit(state, ['setPrimaryModal']);
    case actions.SET_IS_USING_LIQUID:
      return { ...state, isUsingLiquid: action.payload.isUsingLiquid };
    case actions.SET_SEEDPRODUCTSELECTOR_MODAL:
      return action.payload.modalOpen
        ? { ...state, seedProductSelectorModal: true }
        : omit(state, ['seedProductSelectorModal']);
    case actions.SET_CROP_SELECTION_DOES_NOT_MEET_MODAL:
      return {
        ...state,
        cropSelectionDoesNotMeetModal: action.payload.modalOpen
      };
    case actions.SET_IS_OVERLAY_ACTIVE:
      return { ...state, isOverlayActive: action.payload };
    case actions.SET_IS_OVERLAY_QUICK_SELECT_ACTIVE:
      return { ...state, isOverlayQuickSelectActive: action.payload };
    case actions.SET_IS_OVERLAY_SELECT_MODAL_ACTIVE:
      return { ...state, isOverlaySelectModalActive: action.payload };
    case actions.SET_SELECTED_OVERLAY_UUID:
      return { ...state, selectedOverlayUuid: action.payload };
    case actions.SET_SELECTED_OVERLAY_SEASON:
      return { ...state, selectedOverlaySeason: action.payload };
    case actions.SET_SELECTED_OVERLAY_LAYER_GROUP:
      return { ...state, selectedOverlayLayerGroup: action.payload };
    case actions.SET_SELECTED_OVERLAY_LAYER:
      return { ...state, selectedOverlayLayer: action.payload };
    case actions.SET_QUITFLOW_MODAL:
      return { ...state, quitFlowModal: action.payload.modalOpen };
    case actions.SET_PREFERREDDEVICE_MODAL:
      return action.payload
        ? {
          ...state,
          preferredDeviceModal: Object.assign(
            get(state, 'preferredDeviceModal', {}),
            action.payload
          )
        }
        : omit(state, ['preferredDeviceModal']);
    case actions.SET_CONNECT_TO_AFFILIATE_MODAL:
      return action.payload
        ? { ...state, connectToAffiliateModal: action.payload }
        : omit(state, ['connectToAffiliateModal']);
    case actions.TOGGLE_ZONE_SOILS:
      return {
        ...state,
        zoneSoils: {
          ...state.zoneSoils,
          [action.payload.zoneId]: !state.zoneSoils[action.payload.zoneId]
        }
      };
    case actions.SET_ACTIVE_KEYPAD:
      return {
        ...state,
        activeKeypad: action.payload
          ? action.payload
          : {
            applicationType: null,
            property: null,
            value: null,
            zoneId: null
          }
      };

    case actions.SET_PRODUCT_RATE_LEGEND:
      return { ...state, productRateLegend: action.payload.productRateLegend };
    case actions.SET_MAP_WAS_CLICKED:
      return {
        ...state,
        mapWasClickedOnZoneSelect: action.payload.mapWasClickedOnZoneSelect
      };
    case actions.SET_ACTIVE_SCRIPT_CONFIG_VIEW:
      return { ...state, scriptConfigView: action.payload.viewToShow };
    case actions.SET_ACTIVE_SUBSTANCE_TYPE_UUID:
      return { ...state, substanceTypeUuid: action.payload.substanceTypeUuid };
    case actions.SET_ACTIVE_INCORPORATION_METHOD:
      return {
        ...state,
        incorporationMethod: action.payload.incorporationMethod
      };
    case actions.SET_HAS_NITRAPYRIN:
      return { ...state, hasNitrapyrin: action.payload.hasNitrapyrin };
    case actions.SET_EDIT_SCRIPT_EXIT_BUTTON_BEHAVIOR:
      return {
        ...state,
        editScriptExitButton: { navigateUrl: action.payload.navigateUrl }
      };
    case actions.SWITCH_NITROGEN_APPLICATION_RATE:
      return {
        ...state,
        nitrogenApplicationRateType: action.payload.nitrogenApplicationRateType
      };
    case actions.TOGGLE_DRIVE_MODAL:
      return { ...state, isDriveModalActive: action.payload };
    case actions.TOGGLE_ERROR_DRIVE_MODAL:
      return { ...state, isErrorDriveModalActive: action.payload };
    case actions.SHOW_CREATE_JD_WORK_PLAN_MODAL:
      return { ...state, isCreateWorkPlanModalOpen: true};
    case actions.HIDE_CREATE_JD_WORK_PLAN_MODAL:
      return { ...state, isCreateWorkPlanModalOpen: false };
    default:
      return state;
  }
}

// Action Creators

export const setEditScriptExitButtonBehavior = () => {
  return (dispatch, getState) => {
    const state = getState();
    const editButtonBehavior = { navigateUrl: getManageRoute(state) };

    dispatch({
      // TODO - CFVWEB-419 clean up after iOS solution in place.
      // Keyword search "ExitButtonBehavior" to find other things.
      type: actions.SET_EDIT_SCRIPT_EXIT_BUTTON_BEHAVIOR,
      payload: editButtonBehavior
    });
  };
};

export const setExportOptionsIsOpen = ({ exportType, status = false }) => ({
  type: actions.SET_EXPORT_OPTIONS_IS_OPEN,
  payload: { exportType, status }
});

export const setScriptOptionsIsOpen = ({ scriptId, status = false }) => {
  return (dispatch) => {
    dispatch(
      setExportOptionsIsOpen({ exportType: exportTypes.SINGLE, status: false })
    ); // anytime we're messing with the main script options, close the export options
    dispatch({
      type: actions.SET_SCRIPT_OPTIONS_IS_OPEN,
      payload: { scriptId, status }
    });
  };
};

export const setMoreDetailsIsOpen = ({ scriptId, status = false }) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_MORE_DETAILS_IS_OPEN,
      payload: { scriptId, status }
    });
  };
};

export const setSendToDriveIsOpen = (payload) => {
  return {
    type: actions.SET_SEND_TO_DRIVE_IS_OPEN,
    payload
  };
};

export const showCreateJDWorkPlanModal = () => {
  return { type: actions.SHOW_CREATE_JD_WORK_PLAN_MODAL };
};

export const hideCreateJDWorkPlanModal = () => {
  return { type: actions.HIDE_CREATE_JD_WORK_PLAN_MODAL };
};

export const closeDeleteModal = () => {
  return { type: actions.SET_DELETE_MODAL };
};

export const closeConfirmModal = () => {
  return { type: actions.SET_CONFIRM_MODAL };
};

export const closeDuplicateModal = () => {
  return (dispatch) => {
    dispatch(setValidationResult({ key: 'duplicate' }));
    dispatch({ type: actions.SET_DUPLICATE_MODAL });
  };
};

export const closeRenameModal = () => {
  return (dispatch) => {
    dispatch(setValidationResult({ key: 'rename' }));
    dispatch({ type: actions.SET_RENAME_MODAL });
  };
};

export const closeSeedProductSelectorModal = () => {
  return {
    type: actions.SET_SEEDPRODUCTSELECTOR_MODAL,
    payload: { modalOpen: false }
  };
};

export const closeSelectAffiliateOrganizationModal = () => {
  return (dispatch) => {
    dispatch(clearActiveAffiliateOrganizationId());
    dispatch({ type: actions.SET_SELECTORGANIZATION_MODAL });
  };
};

export const closeSetPrimaryModal = () => {
  return { type: actions.SET_SETPRIMARY_MODAL }; // hmmm
};

export const openDeleteModal = (scriptId) => {
  return (dispatch) => {
    dispatch(setScriptOptionsIsOpen({ scriptId }));
    dispatch({
      type: actions.SET_DELETE_MODAL,
      payload: { scriptId }
    });
  };
};

export const openConfirmModal = (scriptId) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_CONFIRM_MODAL,
      payload: { scriptId }
    });
  };
};

export const openDuplicateModal = ({
  scriptId,
  duplicateType = duplicateTypes.NEW,
  uiLocation = eventTypes.MANAGE_OPTIONS_MENU
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const script = getScriptById(state, scriptId);
    const scriptType = getActiveScriptType(state);

    const todaysProductYear = getProductYearByScriptType(state, scriptType);
    const defaultDuplicateScriptName = getMessage(
      'text__prescription-copy-filename',
      { year: todaysProductYear, name: script.name }
    );

    dispatch(setScriptOptionsIsOpen({ scriptId }));
    dispatch({
      type: actions.SET_DUPLICATE_MODAL,
      payload: {
        scriptId,
        duplicateType,
        uiLocation,
        value: defaultDuplicateScriptName
      }
    });
  };
};

export const openSelectAffiliateOrganizationModal = (affiliateId) => {
  return (dispatch, getState) => {
    const state = getState();
    const { organizations } = getAffiliateById(state, affiliateId);

    dispatch({
      type: actions.SET_SELECTORGANIZATION_MODAL,
      payload: { affiliateId, organizations }
    });
  };
};

export const openRenameModal = (scriptId) => {
  return (dispatch, getState) => {
    const state = getState();
    const script = getScriptById(state, scriptId);

    dispatch(setScriptOptionsIsOpen({ scriptId }));
    dispatch({
      type: actions.SET_RENAME_MODAL,
      payload: { scriptId, value: script.name }
    });
  };
};

export const openSetPrimaryModal = (scriptId) => {
  return (dispatch) => {
    dispatch(setScriptOptionsIsOpen({ scriptId }));
    dispatch({
      type: actions.SET_SETPRIMARY_MODAL, // hmmmm
      payload: { scriptId }
    });
  };
};

export const openLayerInclusionModal = (layers) => {
  return {
    type: actions.SET_LAYERINCLUSION_MODAL,
    payload: layers
  };
};

export const closeLayerInclusionModal = () => {
  return {
    type: actions.SET_LAYERINCLUSION_MODAL
  };
};

export const saveNewDuplicateInputValue = (newValue) => {
  return {
    type: actions.SET_DUPLICATE_MODAL,
    payload: { value: newValue }
  };
};

export const saveNewRenameInputValue = (newValue) => {
  return {
    type: actions.SET_RENAME_MODAL,
    payload: { value: newValue }
  };
};

export const setIsUsingLiquid = ({ isUsingLiquid }) => {
  return {
    type: actions.SET_IS_USING_LIQUID,
    payload: { isUsingLiquid }
  };
};

export const setActiveSubstanceTypeUuid = ({ substanceTypeUuid }) => {
  return {
    type: actions.SET_ACTIVE_SUBSTANCE_TYPE_UUID,
    payload: { substanceTypeUuid }
  };
};

export const setActiveIncorporationMethod = ({ incorporationMethod }) => {
  return {
    type: actions.SET_ACTIVE_INCORPORATION_METHOD,
    payload: { incorporationMethod }
  };
};

export const setHasNitrapyrin = ({ hasNitrapyrin }) => {
  return {
    type: actions.SET_HAS_NITRAPYRIN,
    payload: { hasNitrapyrin }
  };
};

export const openSeedProductSelectorModal = () => {
  return (dispatch, getState) => {
    const state = getState();
    const crop = getActiveCrop(state);

    // copy over active products from script to seed selector
    const seedProductInputs = getActiveSeedProductInputs(state);
    Object.keys(seedProductInputs).map((selectId) => {
      const { currentProductId, products } = seedProductInputs[selectId];
      dispatch(
        setSeedSelectorProductInput({ currentProductId, products, selectId })
      );
      return null;
    });

    if (shouldShowWinfieldLink(state)) {
      dispatch(fetchCentroidForActiveFieldId());
    }

    // set up default rm values
    dispatch(initializeSeedSelectorSearchParams(crop));

    // open the modal
    dispatch({
      type: actions.SET_SEEDPRODUCTSELECTOR_MODAL,
      payload: { modalOpen: true }
    });
  };
};

export const setActiveProductRateLegend = () => {
  return (dispatch, getState) => {
    const state = getState();
    const script = getActiveScript(state);
    const productValidationValues = getProductValidationValues(state);
    const productRateLegend = getProductRateLegendForScript({
      state,
      script,
      productValidationValues
    });
    dispatch({
      type: actions.SET_PRODUCT_RATE_LEGEND,
      payload: { productRateLegend }
    });
  };
};

export const setFertilizerProductInput = ({
  active = true,
  currentProductId,
  products = [],
  selectId
}) => {
  return (dispatch) => {
    const sortedProducts = sortBy(products, 'name');
    dispatch(
      setProductInput({
        active,
        applicationType: applicationTypes.FERTILIZER,
        currentProductId,
        products: sortedProducts,
        selectId
      })
    );
  };
};

export const setSeedProductInput = ({
  active = true,
  currentProductId,
  products = [],
  selectId
}) => {
  return (dispatch) => {
    const sortedProducts = sortBy(products, 'name');
    dispatch(
      setProductInput({
        active,
        applicationType: applicationTypes.SEED,
        currentProductId,
        products: sortedProducts,
        selectId
      })
    );
  };
};

export const setProductInputsFromScript = (script) => {
  return (dispatch, getState) => {
    const state = getState();
    const applicationType = getApplicationTypeForScriptType(
      getScriptTypeForScript(script)
    );
    const products = getProductsForScript(script);
    const substanceTypeUuid = getActiveSubstanceTypeUuid(state);
    let productInputProducts = products;
    if (applicationType === applicationTypes.FERTILIZER) {
      const productsForSubstanceType = getFertilizerProductsBySubstanceTypeUuid(
        state,
        substanceTypeUuid
      );
      if (productsForSubstanceType) {
        productInputProducts = productsForSubstanceType;
      }
    }

    if (products.length) {
      products.forEach((product, index) => {
        dispatch(
          setProductInput({
            active: true,
            applicationType,
            currentProductId: product.id,
            products: productInputProducts,
            selectId: `${selectIdPrefix}${index + 1}`
          })
        );
      });
    }
  };
};

export const setProtectionProductForScript = ({ productId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);

    dispatch(updateProtectionProductForZones({ scriptId, productId }));
    dispatch(
      updateProductApplicationPropertyForZones({
        property: applicationProperties.RATE,
        scriptId,
        value: null
      })
    );

    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
    dispatch(setActiveProductRateLegend());
  };
};

export const setProductsForScript = (products, clearExistingProducts) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);

    dispatch(updatePlantingPlanParams(products, clearExistingProducts));
    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
  };
};

export const setFertilizerProductForScript = ({ currentProductId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);

    dispatch(
      updateFertilizerApplicationProductForZones({
        scriptId,
        productId: currentProductId
      })
    );

    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
    dispatch(setActiveProductRateLegend());
  };
};

export const setSeedProductForScript = ({ currentProductId, oldProductId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);

    if (
      currentProductId
      && oldProductId
      && shouldMakeSeedProductDefault(state)
    ) {
      dispatch(
        updateSeedApplicationProductForZones({
          scriptId,
          productId: currentProductId
        })
      );
    } else if (
      currentProductId
      && oldProductId
      && !canAddMoreSeedProductInputs(state)
    ) {
      dispatch(
        updateSeedApplicationProductForZones({
          scriptId,
          productId: currentProductId,
          oldProductId
        })
      );
    } else if (
      currentProductId
      && getActiveSeedProductIds(state).length === 0
    ) {
      dispatch(
        updateSeedApplicationProductForZones({
          scriptId,
          productId: currentProductId
        })
      );
    }

    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
    dispatch(setActiveProductRateLegend());
  };
};

export const addSeedProductInput = () => {
  return (dispatch, getState) => {
    const state = getState();
    const currentNumInputs = getNumActiveSeedProductInputs(state);
    const updatedNumInputs = canAddMoreSeedProductInputs(state)
      ? currentNumInputs + 1
      : currentNumInputs;

    dispatch(
      setSeedProductInput({ selectId: `${selectIdPrefix}${updatedNumInputs}` })
    );
  };
};

export const addSeedProduct = () => {
  return (dispatch, getState) => {
    const state = getState();
    // only open the hybrid selector from the edit screen
    if (isAppMode(state) && isEditRoute(getCurrentPath(state))) {
      dispatch(openSeedProductSelectorModal());
    } else {
      dispatch(addSeedProductInput());
    }
  };
};

export const removeSeedProductInput = ({ selectId }) => {
  return (dispatch, getState) => {
    let state = getState();
    const inputs = getActiveSeedProductInputs(state);
    const { currentProductId: productIdToRemove } = inputs[selectId];
    const scriptId = getActiveScriptId(state);

    dispatch(
      clearSeedApplicationProductForZones({
        productId: productIdToRemove,
        scriptId
      })
    );
    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));

    // this is kind of messed up -- if we're removing the first input and there is a second input
    // existing, take the values from input 2 and move them to input 1 and clear input 2.
    // if there's no second input, then currentProductId and products will
    // get wiped out, effectively clearing it anyway.
    if (selectId.match(`${selectIdPrefix}1`)) {
      const { currentProductId, products } = get(
        inputs,
        `${selectIdPrefix}2`,
        {}
      );
      if (currentProductId) {
        // set input 1 to the contents of input 2
        dispatch(setSeedProductInput({ currentProductId, products, selectId }));
        // update zone applications to the selected product from input 2
        dispatch(
          updateSeedApplicationProductForZones({
            scriptId,
            productId: currentProductId
          })
        );
        // clear & deactivate input 2
        dispatch(
          setSeedProductInput({ active: false, selectId: `${selectIdPrefix}2` })
        );
      } else {
        // just clear input 1
        dispatch(setSeedProductInput({ products, selectId }));
      }
    } else {
      dispatch(setSeedProductInput({ active: false, selectId }));
      dispatch(clearSeedApplicationProductForZones({ scriptId }));

      // refetch state after we just removed a seed product input
      state = getState();
      if (shouldMakeSeedProductDefault(state)) {
        const [defaultSeedProductId] = getActiveSeedProductIds(state);
        dispatch(
          updateSeedApplicationProductForZones({
            scriptId,
            productId: defaultSeedProductId
          })
        );
      }
    }

    // refetch state
    state = getState();
    const activeSeedProductIds = getActiveSeedProductIds(state);

    // if active products ids is  now 1, the the script should get
    // set to the 1 product across the board
    if (activeSeedProductIds.length === 1) {
      dispatch(
        updateSeedApplicationProductForZones({
          scriptId,
          productId: activeSeedProductIds[0]
        })
      );
    }
  };
};

export const clearFertilizerProductInput = ({ selectId }) => {
  return (dispatch, getState) => {
    let state = getState();
    const inputs = getActiveFertilizerProductInputs(state);
    const { products } = get(inputs, `${selectId}`, {});
    dispatch(setFertilizerProductInput({ products, selectId }));
  };
};

// TODO: migrate to finder/processor and refactor the below function as well - CFVWEB-3054
export const searchSeedProductsFromAutocompleteSelect = ({
  advancedOnly = false,
  fragment,
  receiveOptions = () => {}, // Used by crate dropdown - default to noop if receiveOptions is not passed in
  selectId
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const userId = getActiveUserId(state);
    const cropDfpUuid = getActiveCropDfpUuid(state);
    const fieldCountryCode = getActiveFieldCountryCode(state);

    if (fragment && fragment.length >= MIN_HYBRID_PRODUCT_SEARCH_LENGTH) {
      const { currentProductId } = getSeedProductInputBySelectId(
        state,
        selectId
      );
      dispatch(setSeedProductInput({ selectId, currentProductId })); // remove entries that may not match anymore and may get replaced by new results mid-click
      findResource({
        getState,
        dispatch,
        forceWait: true,
        forceReload: true, // Triggers exclusive search - pending searches are discarded
        type: applicationTypes.SEED,
        id: fragment,
        fetcher: () => fetchSeedProductsSearchResultsFromSprout({
          cropDfpUuid,
          advancedOnly,
          fragment,
          userId,
          fieldCountryCode
        }),
        processor: (raw) => {
          const isEmptySignal = Object.values(raw).includes(
            SIGNAL.EMPTY_SERVICE_PAGE
          );
          const products = isEmptySignal ? [] : parseSproutProducts(raw);
          dispatch(
            setApplicationProducts({
              applicationType: applicationTypes.SEED,
              products
            })
          );
          dispatch(
            setSeedProductInput({ products, selectId, currentProductId })
          );
          receiveOptions(products);
        },
        onError: () => {
          dispatch(addErrorToast(getMessage('help-text__failure--search')));
        }
      });
    } else {
      dispatch(
        setApplicationProducts({
          applicationType: applicationTypes.SEED,
          products: []
        })
      );
      receiveOptions([]);
    }
    return null;
  };
};

export const setIsOverlayQuickSelectActive = (active) => {
  return {
    type: actions.SET_IS_OVERLAY_QUICK_SELECT_ACTIVE,
    payload: active
  };
};

export const setIsOverlayActive = (active) => {
  const setOverlayActive = {
    type: actions.SET_IS_OVERLAY_ACTIVE,
    payload: active
  };

  if (!active) {
    return (dispatch) => {
      dispatch(setIsOverlayQuickSelectActive(active));
      dispatch(setOverlayActive);
    };
  }
  return setOverlayActive;
};

export const setIsOverlaySelectModalActive = (active) => {
  return (dispatch) => {
    dispatch(setIsOverlayQuickSelectActive(false));
    dispatch({
      type: actions.SET_IS_OVERLAY_SELECT_MODAL_ACTIVE,
      payload: active
    });
  };
};

export const setSelectedOverlayUuid = (uuid) => {
  return {
    type: actions.SET_SELECTED_OVERLAY_UUID,
    payload: uuid
  };
};

export const setSelectedOverlaySeason = (season) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_SEASON,
      payload: season
    });
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_UUID,
      payload: null
    });
  };
};

export const setSelectedOverlayLayerGroup = (group) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_LAYER_GROUP,
      payload: group
    });
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_LAYER,
      payload: null
    });
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_SEASON,
      payload: null
    });
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_UUID,
      payload: null
    });
  };
};

export const setSelectedOverlayLayer = (layer) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_LAYER,
      payload: layer
    });
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_SEASON,
      payload: null
    });
    dispatch({
      type: actions.SET_SELECTED_OVERLAY_UUID,
      payload: null
    });
  };
};

export const setSelectedOverlaySeasonWithDefaults = ({
  season,
  layer,
  fieldId
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const activeFieldId = fieldId || getActiveFieldId(state);
    const selectedLayer = layer || getSelectedOverlayLayer(state);
    const uuid = getImageUuidsOfLayerForField({
      state,
      fieldId: activeFieldId,
      layer: selectedLayer,
      season
    })[0]; // Default to first
    dispatch(setSelectedOverlayLayer(selectedLayer));
    dispatch(setSelectedOverlaySeason(season));
    dispatch(setSelectedOverlayUuid(uuid));

    // set active Crop if selectedSeason has a Crop, this happens when layer is  either harvest or planting
    const cropActiveLayer = getCropFromActiveImage({
      state,
      layer: selectedLayer,
      fieldId: activeFieldId,
      season });

    if (cropActiveLayer) {
      dispatch(setActiveCropForCurrentMapLayer(cropActiveLayer));
    }
  };
};

export const setSelectedOverlayLayerWithDefaults = (layer, selectedSeason) => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    // fix on quickOverlaySelector; we were setting the incorrect overlayUUID
    const season = selectedSeason || getImageSeasonsOfLayerForField({ state, fieldId, layer })[0]; // Default to first
    dispatch(
      setSelectedOverlayLayerGroup(
        getOverlayLayerGroupTypeOfLayer({ layer, state })
      )
    );
    dispatch(setSelectedOverlayLayer(layer));
    dispatch(setSelectedOverlaySeasonWithDefaults({ season, layer, fieldId }));
  };
};

export const openQuitFlowModal = () => {
  return {
    type: actions.SET_QUITFLOW_MODAL,
    payload: { modalOpen: true }
  };
};

export const closeQuitFlowModal = () => {
  return {
    type: actions.SET_QUITFLOW_MODAL,
    payload: { modalOpen: false }
  };
};

export const closeCropSelectionDoesNotMeetModal = () => ({
  type: actions.SET_CROP_SELECTION_DOES_NOT_MEET_MODAL,
  payload: { modalOpen: false }
});

export const openCropSelectionDoesNotMeetModal = () => ({
  type: actions.SET_CROP_SELECTION_DOES_NOT_MEET_MODAL,
  payload: { modalOpen: true }
});

export const initializeRenameInputValidationResult = (scriptName) => {
  return (dispatch) => {
    const rawValidatorResult = validateScriptName({ scriptName });
    dispatch(
      setValidationResult({
        key: 'rename',
        resultedFromUserInput: false,
        rawValidatorResult
      })
    );
  };
};

export const updateRenameInputValue = (scriptName) => {
  return (dispatch) => {
    const rawValidatorResult = validateScriptName({ scriptName });
    dispatch(setValidationResult({ key: 'rename', rawValidatorResult }));
    dispatch(saveNewRenameInputValue(scriptName));
  };
};

export const validateNewDuplicateInputValue = (scriptName) => {
  return (dispatch) => {
    const rawValidatorResult = validateScriptName({ scriptName });
    const scriptNameIsValid = !rawValidatorResult;

    dispatch(saveNewDuplicateInputValue(scriptName));
    if (scriptNameIsValid) {
      dispatch(setValidationResult({ key: 'duplicate' }));
    } else {
      dispatch(setValidationResult({ key: 'duplicate', rawValidatorResult }));
    }
  };
};

export const openPreferredDeviceModal = (exportType, optionSelected) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    // pull preferred device from what we loaded on init
    const preferredDevice = getPreferredDeviceForScriptType(state) || {};

    dispatch(setScriptOptionsIsOpen({ scriptId }));
    dispatch({
      type: actions.SET_PREFERREDDEVICE_MODAL,
      payload: { exportType, preferredDevice, optionSelected }
    });
  };
};

export const selectPreferredDevice = (preferredDevice) => {
  return {
    type: actions.SET_PREFERREDDEVICE_MODAL,
    payload: { preferredDevice }
  };
};

export const selectMeasurementType = (measurementType) => {
  return {
    type: actions.SET_PREFERREDDEVICE_MODAL,
    payload: { measurementType }
  };
};

export const selectMeasurementDistance = (measurementDistance) => {
  return {
    type: actions.SET_PREFERREDDEVICE_MODAL,
    payload: { measurementDistance }
  };
};

export const closePreferredDeviceModal = () => {
  return {
    type: actions.SET_PREFERREDDEVICE_MODAL
  };
};

export const openConnectToAffiliateModal = (affiliateId) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);

    dispatch(setScriptOptionsIsOpen({ scriptId }));
    dispatch({
      type: actions.SET_CONNECT_TO_AFFILIATE_MODAL,
      payload: affiliateId
    });
  };
};

export const closeConnectToAffiliateModal = () => {
  return {
    type: actions.SET_CONNECT_TO_AFFILIATE_MODAL
  };
};

export const toggleZoneSoils = (zoneId) => {
  return {
    type: actions.TOGGLE_ZONE_SOILS,
    payload: { zoneId }
  };
};

export const setMapWasClickedOnZoneSelect = (mapWasClickedOnZoneSelect) => {
  return {
    type: actions.SET_MAP_WAS_CLICKED,
    payload: { mapWasClickedOnZoneSelect }
  };
};

export const selectZone = ({ mapWasClickedOnZoneSelect, zoneId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const activeZoneId = getActiveZoneId(state);
    const zoneSelectionMode = getZoneSelectionMode(state);

    dispatch(setMapWasClickedOnZoneSelect(mapWasClickedOnZoneSelect));
    switch (zoneSelectionMode) {
      case zoneSelectionModes.DISABLED:
        // don't do anything
        break;
      case zoneSelectionModes.MULTI:
        dispatch(toggleSelectedZone(zoneId));
        break;
      case zoneSelectionModes.SINGLE:
        if (zoneId === activeZoneId) {
          dispatch(toggleSelectedZone(zoneId));
          dispatch(setActiveZoneId({ zoneId: null }));
        } else {
          dispatch(setActiveZoneId({ zoneId }));
        }
        break;
      default:
        logError({ message: 'Unexpected zoneSelectionMode' });
    }
  };
};

/** Remove the active zone selection. This action is only performed in single zone selection mode. */
export const removeActiveZoneSelection = () => {
  return (dispatch, getState) => {
    const state = getState();
    const activeZoneId = getActiveZoneId(state);
    const zoneSelectionMode = getZoneSelectionMode(state);
    if (activeZoneId && zoneSelectionMode === zoneSelectionModes.SINGLE) {
      dispatch(removeSelectedZone(activeZoneId));
    }
  };
};

export const openKeypad = ({ applicationType, property, zoneId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const zone = getActiveScriptZoneById(state, zoneId);
    const application = getApplicationsForZoneByType({
      zone,
      applicationType
    })[0];
    const value
      = get(application, `${property}.q`) || get(application, 'rate.q'); // product-rate and seed-rate are saved as rate

    dispatch({
      type: actions.SET_ACTIVE_KEYPAD,
      payload: { applicationType, property, value, zoneId }
    });
  };
};

export const closeKeypad = ({ revert = false }) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    const {
      applicationType,
      property,
      zoneId,
      value: originalValue
    } = getActiveKeypad(state);

    if (revert) {
      dispatch(
        validateProductApplicationProperty({
          applicationType,
          scriptId,
          zoneId,
          property,
          value: originalValue
        })
      );
      dispatch(
        updateProductApplicationProperty({
          property,
          scriptId,
          value: originalValue,
          zoneId
        })
      );
    }
    dispatch({
      type: actions.SET_ACTIVE_KEYPAD
    });
  };
};

export const blurProductApplicationInput = () => {
  return (dispatch) => {
    dispatch(setActiveProductRateLegend());
  };
};

export const setActiveScriptConfigView = (viewToShow) => {
  return {
    type: actions.SET_ACTIVE_SCRIPT_CONFIG_VIEW,
    payload: { viewToShow }
  };
};

export const populateFertilizerProductInput = ({ substanceTypeUuid }) => {
  return (dispatch, getState) => {
    // clear the fertilizer input before we try to find the real list
    dispatch(
      setFertilizerProductInput({
        products: [],
        selectId: `${selectIdPrefix}1`
      })
    );

    return findFertilizersForSubstanceTypeUuid({
      dispatch,
      getState,
      substanceTypeUuid
    }).then((products) => {
      dispatch(
        setFertilizerProductInput({ products, selectId: `${selectIdPrefix}1` })
      );
      dispatch(
        setApplicationProducts({
          applicationType: applicationTypes.FERTILIZER,
          products,
          substanceTypeUuid
        })
      );
    });
  };
};

export const closeMinimumRequirementsErrorModal = () => {
  return {
    type: actions.SET_EXCLUDED_LAYERS_ERROR_MODAL,
    payload: { modalOpen: false }
  };
};

export const setMinimumRequirementsErrorModal = () => {
  return (dispatch, getState) => {
    const state = getState();
    const validationResult = getLayerValidationResultForToggling(state);
    const isInvalid = get(validationResult, 'isInvalid', false);
    dispatch({
      type: actions.SET_EXCLUDED_LAYERS_ERROR_MODAL,
      payload: { modalOpen: isInvalid }
    });
  };
};

export const setNitrogenApplicationRateType = (nitrogenApplicationRateType) => {
  return {
    type: actions.SWITCH_NITROGEN_APPLICATION_RATE,
    payload: { nitrogenApplicationRateType }
  };
};

export const toggleDriveModal = (payload) => {
  return {
    type: actions.TOGGLE_DRIVE_MODAL,
    payload
  };
};

export const toggleErrorDriveModal = (payload) => {
  return {
    type: actions.TOGGLE_ERROR_DRIVE_MODAL,
    payload
  };
};
